import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { Helmet } from 'react-helmet';
import { useSiteSettings } from '../hooks/useSiteSettings';
import ImagePreviewModal from '../components/ImagePreviewModal';
import './SectionsPage.css';

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  return `${process.env.REACT_APP_API_URL || ''}${src}`;
};

const SectionsPage = () => {
  const { site_name } = useSiteSettings();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imagesMap, setImagesMap] = useState({});
  const [selectedSection, setSelectedSection] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const fetchSections = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/sections`);
        const activeSections = res.ok
          ? (await res.json()).filter(item => item.status === 'active')
          : [];

        let map = {};
        try {
          const artRes = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/articles`);
          if (artRes.ok) {
            const articles = await artRes.json();
            if (Array.isArray(articles)) {
              articles.forEach(article => {
                const sid = article.sectionId?.id || article.sectionId?._id || article.sectionId || null;
                if (sid && article.image) {
                  if (!map[sid]) map[sid] = [];
                  map[sid].push(article.image);
                }
              });
            }
          }
        } catch (error) {
          console.error('خطأ في جلب المقالات:', error);
        }

        const enriched = activeSections.map(section => ({
          ...section,
          displayImage: section.image || (map[section.id] && map[section.id][0]) || ''
        }));
        setSections(enriched);
        setImagesMap(map);
      } catch (error) {
        console.error('خطأ في جلب الأقسام:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSections();
  }, []);

  const openSection = (section) => {
    setSelectedSection(section);
  };

  const selectedImages = selectedSection ? (imagesMap[selectedSection.id] || []) : [];

  return (
    <>
      <Helmet>
        <title>الأقسام | {site_name || ''}</title>
      </Helmet>
      <div className="section">
        <h1 className="section-title">أقسام المحتوى</h1>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>جاري تحميل الأقسام...</p>
          </div>
        ) : sections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>لا توجد أقسام لعرضها حالياً.</p>
          </div>
        ) : (
          <div className="sections-grid">
            {sections.map(section => (
              <div
                key={section.id}
                className="section-card"
                role="button"
                tabIndex={0}
                onClick={() => openSection(section)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openSection(section);
                  }
                }}
              >
                <div className="section-card-image-wrap">
                  {section.displayImage ? (
                    <img
                      src={resolveImage(section.displayImage)}
                      alt={section.name}
                      className="section-card-image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="section-card-image-placeholder">
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="section-card-body">
                  <h3 className="section-card-title">{section.name}</h3>
                  <p className="section-card-desc">
                    {section.description || 'تفاصيل القسم'}
                  </p>
                  <span className="section-card-btn">
                    عرض الصور
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      style={{ transform: 'rotate(180deg)' }}
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={!!selectedSection}
        onCancel={() => setSelectedSection(null)}
        footer={null}
        title={selectedSection ? `صور ${selectedSection.name}` : ''}
        width={620}
        centered
      >
        {selectedImages.length > 0 ? (
          <div className="sections-modal-grid">
            {selectedImages.map((img, index) => (
              <div
                className="sections-modal-item"
                key={index}
                role="button"
                tabIndex={0}
                onClick={() => setPreviewImage(resolveImage(img))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPreviewImage(resolveImage(img));
                  }
                }}
              >
                <img src={resolveImage(img)} alt={`${selectedSection.name} - ${index + 1}`} loading="lazy" />
              </div>
            ))}
          </div>
        ) : (
          <div className="sections-modal-empty">لا توجد صور لهذا القسم.</div>
        )}
      </Modal>

      <ImagePreviewModal
        open={!!previewImage}
        image={previewImage}
        alt={selectedSection ? selectedSection.name : ''}
        onClose={() => setPreviewImage(null)}
      />
    </>
  );
};

export default SectionsPage;
